import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo, faSave } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { Controls } from 'src/app/controls/forms/managed-form/ngx-sub-form-utils';
import { DataInput } from 'src/app/controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from 'src/app/controls/forms/management-form/management-form';
import { IEmailAddress } from 'src/app/models/interfaces/email-address.interface';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { EmailAddressFormContext } from '../email-address-form-context';

@Component({
    selector: 'app-email-address-form',
    templateUrl: './email-address-form.component.html',
    styleUrls: ['./email-address-form.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        FaIconComponent,
        MatTooltipModule,
        MatCheckboxModule,
        MatButtonModule,
        NgClass,
    ],
})
export class EmailAddressFormComponent extends ManagementFormComponent<IEmailAddress> implements OnInit, OnChanges {
    public faSave = faSave;
    public faCircleInfo = faCircleInfo;

    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Infos du emailAddress à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('emailAddress')
    public dataInput: Required<IEmailAddress> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: EmailAddressFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<EmailAddressFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: EmailAddressFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('emailAddressUpdated')
    public dataOutput: EventEmitter<IEmailAddress> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    public emailAddressType = [];

    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.item);
        });

        this.formGroup.get('address').setValidators([Validators.required, Validators.pattern(this.emailPattern)]);
        if (this._context.mode === FormMode.Edition) {
            this.formGroup.get('address').disable();
        }
    }

    /**
     * Utilisé pour rendre le champ id read-only
     */
    ngOnChanges() {
        this.formGroup.get('id').disable();
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<IEmailAddress> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            organizationId: new UntypedFormControl(null),
            address: new UntypedFormControl(null),
            isActive: new UntypedFormControl(null),
            sendingEnabled: new UntypedFormControl(null),
            emailDisplayName: new UntypedFormControl(null),
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        this.dataValue.address = this.formGroup.get('address').value;
        super.manualSave();
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
